import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { first } from 'rxjs';
import { CommonService } from 'src/app/services/common.service';
import { BrowserDataService } from 'src/app/services/browser-data.service';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SearchPipe } from 'src/app/search.pipe';
import { NgxPaginationModule } from 'ngx-pagination';
import { PaginationComponent } from 'src/app/helpers/pagination/pagination.component';

interface Browser {
  id: string;
  browser: string;
  browserVersion: string;
  os: string;
  ip: string;
  port: number;
  automationPort: number;
  username: string;
  password: string;
  accessUrl?: string;
  automationUrl?: string;
  ngasa_url?: string;
  user?: any;
}

@Component({
  selector: 'app-browsers',
  templateUrl: './browsers.component.html',
  styleUrls: ['./browsers.component.css'],
  standalone: true,
  imports: [TranslateModule, NgxPaginationModule, CommonModule, FormsModule, SearchPipe, PaginationComponent]
})
export class BrowsersComponent implements OnInit {

  @ViewChild('alert_message') alertTemplate: TemplateRef<any>;

  alertMessage: any;
  modalRef!: MatDialogRef<any>;

  browserPageSize: any;
  confirmRemove = { value: true };
  filteredBrowsers: any;
  browsersBySerial: any = {};
  search: any;

  showBrowserForm: boolean = false;
  isEditMode: boolean = false;
  isViewMode: boolean = false;

  page = 1;
  _browserId: any;

  removeBrowsersSearch: any;
  removeBrowsersFilteredBrowser: any;
  removeBrowserId: any;

  currentBrowser: Browser = {
    id: '',
    browser: '',
    browserVersion: '',
    os: '',
    ip: '',
    port: 0,
    automationPort: 0,
    username: '',
    password: '',
    accessUrl: '',
    automationUrl: '',
    ngasa_url: '',
    user: null
  };

  browsers: Browser[] = [];

  itemsPerPageOptions = [
    { name: '1', value: 1 },
    { name: '5', value: 5 },
    { name: '10', value: 10 },
    { name: '20', value: 20 },
    { name: '50', value: 50 },
    { name: '100', value: 100 },
    { name: '200', value: 200 },
    { name: '500', value: 500 },
    { name: '1000', value: 1000 },
    { name: '*', value: 0 }
  ];

  constructor(
    private commonService: CommonService,
    private browserDataService: BrowserDataService,
    private dialog: MatDialog,
  ) { }

  ngOnInit() {
    this.initScope();
    this.browserPageSize = 10;
  }

  onSearch(searchData: any) {
    this.search = searchData;
    this.page = 1; // Reset to first page on search
  }

  onItemPerPageChange(changedItemPerPage: any) {
    this.browserPageSize = changedItemPerPage;
    this.page = 1; // Reset to first page on page size change
  }

  onNextPageChange(nextPage: number) {
    this.page = nextPage;
  }

  publishBrowser(browser: Browser) {
    // Process browser data if needed
    for (let i in browser) {
      if (browser[i] === null) {
        browser[i] = '';
      }
    }
    return browser;
  }

  addBrowser(browser: Browser, timeStamp: any) {
    return this.commonService.add(
      this.browsers,
      this.browsersBySerial,
      browser,
      'id',
      timeStamp
    );
  }

  updateBrowser(browser: Browser, timeStamp: any) {
    return this.commonService.update(
      this.browsers,
      this.browsersBySerial,
      browser,
      'id',
      timeStamp
    );
  }

  deleteBrowser(id: any, timeStamp: any) {
    return this.commonService.delete(
      this.browsers,
      this.browsersBySerial,
      id,
      timeStamp
    );
  }

  initScope() {
    let user = JSON.parse(localStorage.getItem('currentUser'))
    const group = user.userObj.group;
    this.browserDataService.getBrowserList(group).pipe(first()).subscribe((res: any) => {
      // If the API returns the array directly without a `success` field:
      if (Array.isArray(res)) {
        res.forEach((browser) => {
          this.addBrowser(this.publishBrowser(browser), -1);
        });
      }
      // If wrapped with `success` and `browsers` or `data` key
      else if (res.success === true || res.success === "true") {
        const browsers: Browser[] = res.browsers || res.data || [];
        browsers.forEach((browser) => {
          this.addBrowser(this.publishBrowser(browser), -1);
        });
      }
    });
  }

  // Browser Form Operations
  openBrowserForm() {
    this.resetForm();
    this.isEditMode = false;
    this.isViewMode = false;
    this.showBrowserForm = true;
  }

  editBrowser(browser: Browser) {
    this.currentBrowser = { ...browser };
    this.isEditMode = true;
    this.isViewMode = false;
    this.showBrowserForm = true;
  }

  viewBrowser(browser: Browser) {
    this.currentBrowser = { ...browser };
    this.isEditMode = false;
    this.isViewMode = true;
    this.showBrowserForm = true;
  }

  closeBrowserForm() {
    this.showBrowserForm = false;
    this.resetForm();
  }

  resetForm() {
    this.currentBrowser = {
      id: '',
      browser: '',
      browserVersion: '',
      os: '',
      ip: '',
      port: 0,
      automationPort: 0,
      username: '',
      password: '',
      accessUrl: '',
      automationUrl: '',
      ngasa_url: '',
      user: null
    };
  }

  saveBrowser() {
    if (this.isEditMode) {
      // Update existing browser
      this.browserDataService.updateBrowser(this.currentBrowser).pipe(first()).subscribe(
        (res: any) => {
          if (res.success == true || res.success == "true") {
            this.updateBrowser(this.currentBrowser, -1);
            this.closeBrowserForm();
            this.openAlertMod(this.alertTemplate, 'Browser updated successfully');
          } else {
            this.openAlertMod(this.alertTemplate, res.message || 'Failed to update browser');
          }
        },
        (error) => {
          console.error('Error updating browser:', error);
          this.openAlertMod(this.alertTemplate, 'Error updating browser');
        }
      );
    } else {
      // Add new browser
      this.browserDataService.saveBrowser(this.currentBrowser).pipe(first()).subscribe(
        (res: any) => {
          if (res.success == true || res.success == "true") {
            this.addBrowser(this.currentBrowser, -1);
            this.closeBrowserForm();
            this.openAlertMod(this.alertTemplate, 'Browser added successfully');
          } else {
            this.openAlertMod(this.alertTemplate, res.message || 'Failed to add browser');
          }
        },
        (error) => {
          console.error('Error adding browser:', error);
          this.openAlertMod(this.alertTemplate, 'Error adding browser');
        }
      );
    }
  }

  // Remove Operations
  remove(browserId?: any, template?: TemplateRef<any>) {
    this._browserId = browserId;
    this.modalRef = this.dialog.open(template, { width: '300px' });
  }

  removeBrowser() {
    this.browserDataService.deleteBrowser(this._browserId).pipe(first()).subscribe(
      (res: any) => {
        if (res.success == true || res.success == "true") {
          this.deleteBrowser(this._browserId, -1);
          this.cancelRequest();
          this.openAlertMod(this.alertTemplate, res.message || 'Browser removed successfully');
        } else {
          this.openAlertMod(this.alertTemplate, res.message || 'Failed to remove browser');
        }
      },
      (error) => {
        console.error('Error removing browser:', error);
        this.openAlertMod(this.alertTemplate, 'Error removing browser');
        this.cancelRequest();
      }
    );
  }

  removeBrowsers(search?: any, filteredBrowsers?: any, askConfirmation?: any, template?: TemplateRef<any>) {
    this.cancelRequest();
    let _search = search;
    let _filteredBrowsers = filteredBrowsers;

    if (this.confirmRemove.value) {
      _search = this.removeBrowsersSearch;
      _filteredBrowsers = this.removeBrowsersFilteredBrowser;
    }

    let removeBrowsers = () => {
      // Call API to remove all browsers
      this.browserDataService.deleteAllBrowsers().pipe(first()).subscribe(
        (res: any) => {
          if (res.success == true || res.success == "true") {
            this.browsers = [];
            this.browsersBySerial = {};
            this.openAlertMod(this.alertTemplate, res.message || 'All browsers removed successfully');
          } else {
            this.openAlertMod(this.alertTemplate, res.message || 'Failed to remove browsers');
          }
        },
        (error) => {
          console.error('Error removing all browsers:', error);
          this.openAlertMod(this.alertTemplate, 'Error removing all browsers');
        }
      );
    };

    if (askConfirmation) {
      this.removeBrowsersSearch = search;
      this.removeBrowsersFilteredBrowser = filteredBrowsers;
      this.modalRef = this.dialog.open(template, { width: '300px' });
    } else {
      removeBrowsers();
    }
  }

  // Pagination
  handlePageChange(event: any) {
    this.page = event.pageIndex + 1;
  }

  // Modals
  openAlertMod(template: TemplateRef<any>, message: any) {
    this.modalRef = this.dialog.open(template, { width: '300px' });
    this.alertMessage = message;
    setTimeout(() => {
      this.modalRef.close();;
      this.alertMessage = "";
    }, 2000);
  }

  cancelRequest() {
    this.modalRef.close();
  }
}